import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import AppHeader from "../components/AppHeader";
import type { CohortRow } from "../lib/types";
import {
  fetchGradingQueue,
  fetchSubmissionAnswersForAdmin,
  setManualGrade,
  type GradingQueueItem,
  type AnswerMap,
} from "../lib/submissions";
import { getAssignment } from "../content";

// Admin/instructor view. Reachable only via RequireAdmin.
// Day 2 shell listed cohorts; Day 5 adds the MANUAL-GRADE QUEUE: submissions
// whose free_response items await instructor review. The instructor reads the
// student's answers and marks them complete/incomplete (with an optional note);
// the overall pass state recomputes from the stored auto-score + manual verdict.
export default function Admin() {
  const { user, isAdmin, student } = useAuth();
  const [cohorts, setCohorts] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    supabase
      .from("cohorts")
      .select(
        "id, name, slug, seat_cap, seats_sold, status, session1_at, session2_at, session3_at, zoom_join_url"
      )
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        if (!active) return;
        setCohorts((data as CohortRow[] | null) ?? []);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-txcfi-ink">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Instructor admin</h1>
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
            Admin
          </span>
        </div>
        <p className="mt-1 text-slate-600">
          Signed in as {user?.email}
          {isAdmin ? "" : " (no admin access)"}.
        </p>

        {/* Manual grading queue (Day 5) */}
        <GradingQueue graderStudentId={student?.id ?? null} />

        <section className="mt-12">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Cohorts
          </h2>
          {loading ? (
            <p className="mt-2 text-slate-400">Loading…</p>
          ) : cohorts.length === 0 ? (
            <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-slate-500">
              No cohorts yet (or the database isn&apos;t connected). Apply the
              migrations + seed to populate the test cohort.
            </div>
          ) : (
            <table className="mt-3 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-sm">
              <thead className="bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Cohort</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Seats</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium">{c.name}</td>
                    <td className="px-4 py-2 text-slate-600">{c.status}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {c.seats_sold}/{c.seat_cap}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <p className="mt-8 text-xs text-slate-400">
          Roster and cohort export arrive on later sprint days.
        </p>
      </main>
    </div>
  );
}

// ── Manual grading queue ─────────────────────────────────────────────────────
function GradingQueue({ graderStudentId }: { graderStudentId: string | null }) {
  const [items, setItems] = useState<GradingQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchGradingQueue());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load grading queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Needs manual grading
        </h2>
        <button
          onClick={() => void reload()}
          className="text-xs text-slate-500 hover:text-slate-800"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="mt-2 text-slate-400">Loading queue…</p>
      ) : error ? (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="mt-2 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-slate-500">
          Nothing awaiting manual review. Scenario / short-answer responses appear
          here after a student submits an assignment that contains them.
        </div>
      ) : (
        <ul className="mt-3 space-y-4">
          {items.map((item) => (
            <GradingCard
              key={item.status.id}
              item={item}
              graderStudentId={graderStudentId}
              onGraded={reload}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function GradingCard({
  item,
  graderStudentId,
  onGraded,
}: {
  item: GradingQueueItem;
  graderStudentId: string | null;
  onGraded: () => Promise<void>;
}) {
  const { status, lessonTitle, lessonBodyRef, studentName, studentEmail } = item;
  const [answers, setAnswers] = useState<AnswerMap | null>(null);
  const [loadingAns, setLoadingAns] = useState(false);
  const [note, setNote] = useState(status.manual_note ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The file-authored question set so we can show prompts for free_response items.
  const assignment = getAssignment(lessonBodyRef);
  const freeResponseQs =
    assignment?.questions.filter((q) => q.type === "free_response") ?? [];

  const loadAnswers = useCallback(async () => {
    setLoadingAns(true);
    setErr(null);
    try {
      const map = await fetchSubmissionAnswersForAdmin(
        status.enrollment_id,
        status.lesson_id,
      );
      setAnswers(map);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load answers.");
    } finally {
      setLoadingAns(false);
    }
  }, [status.enrollment_id, status.lesson_id]);

  useEffect(() => {
    void loadAnswers();
  }, [loadAnswers]);

  async function grade(manualPass: boolean) {
    setSaving(true);
    setErr(null);
    const { error } = await setManualGrade({
      statusId: status.id,
      autoPassed: status.auto_passed,
      manualPass,
      note: note.trim() || null,
      graderStudentId,
    });
    setSaving(false);
    if (error) {
      setErr(error);
      return;
    }
    await onGraded();
  }

  return (
    <li className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="font-semibold text-slate-900">
            {lessonTitle ?? "Assignment"}
          </p>
          <p className="text-sm text-slate-500">
            {studentName ?? studentEmail ?? "Student"}
            {studentName && studentEmail ? ` · ${studentEmail}` : ""}
          </p>
        </div>
        <span className="text-xs text-slate-400">
          Auto score:{" "}
          {status.auto_percent != null ? `${status.auto_percent}%` : "—"}
          {status.auto_passed === false ? " (auto-fail)" : ""}
        </span>
      </div>

      <div className="mt-4 space-y-4">
        {loadingAns ? (
          <p className="text-sm text-slate-400">Loading answers…</p>
        ) : freeResponseQs.length === 0 ? (
          <p className="text-sm text-slate-400">
            No free-response questions found for this assignment.
          </p>
        ) : (
          freeResponseQs.map((q) => (
            <div
              key={q.id}
              className="rounded border border-slate-100 bg-slate-50 p-3"
            >
              <p className="text-sm font-medium text-slate-700">{q.prompt}</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-900">
                {answers && answers[q.id] != null && answers[q.id] !== ""
                  ? String(answers[q.id])
                  : "— (no answer)"}
              </p>
            </div>
          ))
        )}
      </div>

      <div className="mt-4">
        <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Instructor note (optional)
        </label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          placeholder="Feedback for the student…"
        />
      </div>

      {err && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => void grade(true)}
          disabled={saving}
          className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Mark complete"}
        </button>
        <button
          onClick={() => void grade(false)}
          disabled={saving}
          className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          Mark incomplete
        </button>
      </div>
    </li>
  );
}
